import { InvitationStatusEnum, PermissionEnum, RoleEnum } from 'scalis-db-v2'
import { CompanyWorkspace, Company, TokenCurrentCompany } from '~/types/auth/TokenPayload'
import { prismaClientScalisV2 as prisma } from '~/utils/prismaClientScalisV2'
import { OrganizationRole } from '~/v2/enums'

type UserInvitation = NonNullable<Awaited<ReturnType<typeof getUser>>>['UserInvitation'][number]

export const isUserScalisAdmin = (user: Awaited<ReturnType<typeof getUser>>): boolean => {
  if (!user?.UserInvitation) return false

  return user.UserInvitation.some((invitation) =>
    invitation.CompanyUser?.UserRoles.some((userRole) => userRole.Role.name === RoleEnum.SCALIS_ADMIN),
  )
}

const mapUserPermissions = (currentCompany: UserInvitation) => {
  const permissions: PermissionEnum[] = []

  if (!currentCompany.CompanyUser) {
    return permissions
  }

  for (let i = 0; i < currentCompany.CompanyUser.UserRoles.length; i++) {
    const userRole = currentCompany.CompanyUser.UserRoles[i]
    for (let j = 0; j < userRole.Role.RolePermissions.length; j++) {
      const rolePermission = userRole.Role.RolePermissions[j]
      permissions.push(rolePermission.Permission.name as PermissionEnum)
    }
  }

  return permissions
}

export const getUserByEmail = async (email: string) => {
  return prisma.scalisUser.findFirst({
    where: {
      EmailVerification: {
        some: {
          emailAddress: email.toLowerCase(),
        },
      },
    },
    select: {
      id: true,
      identityProviderId: true,
      identityProviderUsername: true,
      firstName: true,
      lastName: true,
      activeStatus: true,
      profileImage: true,
      completedAt: true,
      EmailVerification: true,
      UserInvitation: {
        include: {
          Company: {
            include: {
              CompanyInfo: true,
            },
          },
        },
      },
    },
  })
}

export const getUser = async (id: number) => {
  return prisma.scalisUser.findFirst({
    where: {
      id,
    },
    select: {
      id: true,
      identityProviderId: true,
      identityProviderUsername: true,
      firstName: true,
      lastName: true,
      activeStatus: true,
      profileImage: true,
      completedAt: true,
      EmailVerification: true,
      UserInvitation: {
        include: {
          Company: {
            include: {
              CompanyInfo: true,
            },
          },
          CompanyUser: {
            include: {
              UserRoles: {
                include: {
                  Role: {
                    include: {
                      RolePermissions: {
                        include: {
                          Permission: true,
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  })
}

export const filterUserInvitations = (userInvitations: UserInvitation[]) => {
  if (!userInvitations) return []

  return userInvitations.filter(
    (userInvitation) => userInvitation.invitationStatus === InvitationStatusEnum.INVITE_ACCEPTED,
  )
}

export const getUserInvitation = (userInvitations: UserInvitation[], subdomain: string | null | undefined) => {
  const currentUserInvitation = userInvitations?.find(
    (userInvitation) => userInvitation.Company?.subdomain === subdomain,
  )

  return currentUserInvitation
}

export const getUserCompanies = (scalisUser: Awaited<ReturnType<typeof getUser>>) => {
  if (!scalisUser) return []

  const userInvitations = filterUserInvitations(scalisUser.UserInvitation)

  const companies: Company[] =
    userInvitations?.map((userInvitation) => ({
      id: userInvitation.Company?.id ?? 0,
      subdomain: userInvitation.Company?.subdomain ?? '',
      imageSrc: userInvitation.Company?.CompanyInfo?.logo ?? '',
      name: userInvitation.Company?.CompanyInfo?.companyName ?? '',
    })) ?? []

  return companies
}

const getWorkspaceData = (
  subdomain: string | null,
  scalisUser: Awaited<ReturnType<typeof getUser>>,
): CompanyWorkspace => {
  if (!scalisUser) {
    return {
      currentCompany: null,
      companyUser: null,
    }
  }

  const userInvitations = filterUserInvitations(scalisUser.UserInvitation)

  const userInvitation = getUserInvitation(userInvitations, subdomain)

  const currentCompany: TokenCurrentCompany | null = userInvitation
    ? {
        id: userInvitation.Company?.id ?? 0,
        subdomain: userInvitation.Company?.subdomain ?? '',
        name: userInvitation.Company?.CompanyInfo?.companyName ?? '',
        imageSrc: userInvitation.Company?.CompanyInfo?.logo ?? '',
        permissions: mapUserPermissions(userInvitation),
        roles:
          (userInvitation.CompanyUser?.UserRoles.map((userRole) => userRole.Role.name!).filter(
            Boolean,
          ) as (keyof typeof OrganizationRole)[]) || [],
      }
    : null

  let companyUser = null
  if (userInvitation?.CompanyUser?.id) {
    companyUser = {
      // only pass important properties to reduce cookie size
      id: userInvitation?.CompanyUser?.id ?? 0,
      companyId: userInvitation?.CompanyUser?.companyId ?? 0,
      userInvitationId: userInvitation?.CompanyUser?.userInvitationId ?? 0,
      email: userInvitation?.email ?? null,
    }
  }

  return {
    currentCompany,
    companyUser,
  }
}

export { getWorkspaceData }
